import React, { useState, useMemo, useRef, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import "./style.css";
import { Breadcrumb, OptionList, Title } from "../../Components/Atoms";
import { ReactTable } from "../../Components/Molecules";
import { DownloadIcon, VerifiedIcon } from "../../Assets/images/svgs";
import PATHS from "../../Routes/paths";
import { Button, Modal, ModalOverlay, ModalContent, ModalCloseButton, Spinner, useToast } from "@chakra-ui/react";
import { getLocalisedDate, getLocalisedTime } from "../../Library/datetime";
import AddPayment from "../Payments/addPaymentModal";
import { getRenewalHistory, submitPaymentDetails, getAllDealers } from "../../APIs";
import { jsonToCSVConvertor } from "../../Library/jsonUtils";
import { StoreContext } from "../../Library/contexts";

const RENEWAL_STATUSES = [
  { idx: 1, label: "Requested" },
  { idx: 2, label: "PI raised" },
  { idx: 3, label: "Payment Done" },
  { idx: 4, label: "Renewed" },
];

const RenewalDetail = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const requestId = searchParams.get("reqId");
  const { allDealers, setAllDealers } = useContext(StoreContext);

  const [renewalDetails, setRenewalDetails] = useState(null);
  const [devicesData, setDevicesData] = useState([]);
  const [loading, setLoading] = useState(true);

  const renewalStatusesRef = useRef({});
  const [timelineLoaded, setTimelineLoaded] = useState(false);

  const [isAddPaymentModalVisible, setIsAddPaymentModalVisible] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [isPaymentDetailAvailable, setIsPaymentDetailAvailable] = useState(false);

  const toast = useToast();

  // Fetch dealers if not in context
  useEffect(() => {
    if (!allDealers || !allDealers.length) {
      getAllDealers(null, (err, dealers) => {
        if (err) console.error(err);
        else
          setAllDealers([
            { label: "All Dealerships", value: "" },
            ...dealers.map((d) => ({ label: d.nm, value: d.idx }))
          ]);
      });
    }
  }, [allDealers, setAllDealers]);

  useEffect(() => {
    if (!allDealers || !allDealers.length || !requestId) return;
    setLoading(true);
    getRenewalHistory({ idx: requestId })
      .then((response) => {
        let data = response;

        if (response.data) {
          data = response.data;
        }

        if (Array.isArray(data)) {
          data = data.length > 0 ? data[0] : null;
        }

        if (!data) {
          console.error("No renewal data found");
          setLoading(false);
          return;
        }

        setRenewalDetails({
          idx: data.idx,
          quantity: data.deviceCount,
          status: getStatusLabel(data.status),
          currentStep: data.status,
          pfInvNm: data.pfInvNm,
          pfInvUrlPrefix: data.pfInvUrlPrefix
        });

        setDevicesData((data.devices || []).map((d, i) => {
          const dealerObj = allDealers.find(ad => ad.value === d.dlrId);
          return {
            idx: i + 1,
            imei: d.imei,
            regNumber: d.regNo,
            dealership: dealerObj ? dealerObj.label : (d.dealership || d.dlr || "-"),
            expiryDate: d.sed ? getLocalisedDate(d.sed, true) : "-",
            sim: d.sim,
            chassisNo: d.chNum,
            status: d.status,
            model: d.model,
            make: d.make,
            sed: d.sed
          };
        }));

        if (data.timeline) {
          data.timeline.forEach(t => {
            renewalStatusesRef.current[t.status] = {
              stat: t.status,
              ts: t.ts
            };
          });
        }

        if (data.paymentDetails) {
          let screenshotUrl = null;
          if (data.paymentDetails.screenshotUrl) {
            screenshotUrl = data.paymentDetails.screenshotUrl;
          } else if (data.screenshotUrlBase) {
            screenshotUrl = data.screenshotUrlBase + '/' + data.paymentDetails.screenshotUrl;
          }

          setPaymentDetails({
            refNo: data.paymentDetails.refNo,
            amt: data.paymentDetails.amount,
            ts: data.paymentDetails.ts,
            ssUrl: screenshotUrl
          });
          setIsPaymentDetailAvailable(true);
        }

        setTimelineLoaded(true);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching renewal details:", err);
        setLoading(false);
      });

  }, [requestId, allDealers]);

  const getStatusLabel = (status) => {
    switch (status) {
      case 1: return "Renewal Requested";
      case 2: return "PI Raised";
      case 3: return "Payment Done";
      case 4: return "Renewed";
      default: return "Unknown";
    }
  };

  const devicesColumns = useMemo(() => [
    {
      Header: "Device IMEI",
      accessor: "imei",
      Cell: ({ row, value }) => (
        <Link
          to={`/show-dealers/specific-device?imei=${value}&installed=true&isDm=true${row.original.sed ? `&sed=${row.original.sed}` : ''}`}
          style={{ color: "var(--ButtonPrimary)" }}
        >
          {value}
        </Link>
      )
    },
    { Header: "Registration Number", accessor: "regNumber" },
    { Header: "Dealership", accessor: "dealership" },
    { Header: "Expiry Date", accessor: "expiryDate" },
  ], []);

  const breadcrumbPaths = [
    { idx: 1, name: "Renewal History", link: PATHS.DEVICE_SUBSCRIPTION },
    { idx: 2, name: `Renewal Detail${renewalDetails ? ` - R${String(renewalDetails.idx).padStart(3, '0')}` : ''}`, isCurrentPage: true },
  ];

  const downloadOptions = [
    { label: "Download All", value: 1 },
  ];

  const handleDownload = () => {
    if (devicesData.length === 0) {
      return;
    }

    const sendToExcel = devicesData.map(d => ({
      'Device IMEI': d.imei || '-',
      'Registration Number': d.regNumber || '-',
      'Dealership': d.dealership || '-',
      'Expiry Date': d.expiryDate || '-',
      'SIM Number': d.sim || '-',
      'Model': d.model || '-',
      'Make': d.make || '-',
      'Chassis Number': d.chassisNo || '-',
      'Status': d.status === 1 ? 'Active' : d.status === 2 ? 'Expiring' : d.status === 3 ? 'Expired' : d.status === 4 ? 'SIM Inactive' : 'Unknown'
    }));

    const args = "Device IMEI, Registration Number, Dealership, Expiry Date, SIM Number, Model, Make, Chassis Number, Status";
    const sendData = sendToExcel.map(exceldata =>
      args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {})
    );

    const reqId = renewalDetails?.idx ? `R${String(renewalDetails.idx).padStart(3, '0')}` : 'Renewal';
    jsonToCSVConvertor(sendData, "", true, `${reqId}_Devices_${Date.now()}`, false);
  };

  const handleViewPI = () => {
    if (!renewalDetails?.pfInvNm || !renewalDetails?.pfInvUrlPrefix) return;
    const link = document.createElement("a");
    link.href = `${renewalDetails.pfInvUrlPrefix}/${encodeURIComponent(renewalDetails.pfInvNm)}`;
    link.style = "visibility:hidden";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closeHandler = () => {
    setIsAddPaymentModalVisible(false);
  };

  const onPaymentAdded = (payment, file) => {
    const payload = {
      refNo: payment.refNo,
      amount: payment.amt,
      ts: payment.ts || new Date().getTime(),
    };

    const files = file ? [file] : [];

    submitPaymentDetails(renewalDetails.idx, payload, files)
      .then((response) => {
        const responseData = response || {};
        const paymentData = responseData.paymentDetails || payload;

        let screenshotUrl = null;
        if (paymentData.screenshotUrl) {
          screenshotUrl = paymentData.screenshotUrl;
        }

        setPaymentDetails({
          refNo: paymentData.refNo,
          amt: paymentData.amount,
          ts: paymentData.ts,
          ssUrl: screenshotUrl
        });

        setIsPaymentDetailAvailable(true);

        const updateDetails = { stat: 3, ts: paymentData.ts };
        renewalStatusesRef.current[3] = updateDetails;

        setTimelineLoaded(prev => !prev);
        closeHandler();
      })
      .catch((err) => {
        console.error("Error submitting payment:", err);
        toast({
          title: "Payment Submission Failed",
          description: err.message || "Unable to submit payment details. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top"
        });
        closeHandler();
      });
  };

  const handleSubmitPaymentDetails = () => {
    setIsAddPaymentModalVisible(true);
  };

  const renderTimelinePoints = () => {
    return RENEWAL_STATUSES.map((status, idx) => {
      const details = renewalStatusesRef.current[status.idx] || { stat: status.idx };
      const isCompleted = !!details.ts;

      const nextStatus = RENEWAL_STATUSES[idx + 1];
      const nextDetails = nextStatus ? renewalStatusesRef.current[nextStatus.idx] || {} : {};
      const isStepComplete = !!nextDetails.ts;

      const isPiRaised = !!renewalStatusesRef.current[2]?.ts;

      return (
        <div className={`timeline-point ${isCompleted ? "" : "update-text-color"}`} key={status.idx}>
          <p className="point-status">{status.label}</p>
          {isCompleted && (
            <div className="timeline-point-ts">
              <p>{getLocalisedDate(details.ts, true)}</p>
              <p>{getLocalisedTime(details.ts, true, true)}</p>
            </div>
          )}

          {status.idx === 2 && isCompleted && renewalDetails?.pfInvNm && renewalDetails?.pfInvUrlPrefix && (
            <div className="view-btn">
              <span onClick={handleViewPI}>View PI</span>
            </div>
          )}

          {status.idx === 3 && isPiRaised && !isCompleted && (
            <div className="timeline-point-btn-container">
              <Button className="timeline-btn" onClick={handleSubmitPaymentDetails}>
                Submit Payment Details
              </Button>
            </div>
          )}

          {status.idx === 3 && isCompleted && isPaymentDetailAvailable && (
            <div className="view-btn">
              <span onClick={() => setShowPaymentDetails(true)}>View Payment Details</span>
            </div>
          )}

          {idx !== (RENEWAL_STATUSES.length - 1) && (
            <div className={`timeline-dashed-line ${isStepComplete ? "update-border-color" : ""}`}></div>
          )}

          {isCompleted ? (
            <VerifiedIcon className="verified-icon" />
          ) : (
            <span className="timeline-bullet"></span>
          )}
        </div>
      );
    });
  };

  return (
    <section className="panel-main renewal-detail-page">
      <header className="renewal-detail-header">
        <Breadcrumb paths={breadcrumbPaths} />
      </header>

      <article className="renewal-detail-content">
        {loading ? (
          <div className="renewal-loader-container">
            <Spinner size="xl" />
          </div>
        ) : (
          <>
            <div className="renewal-detail-card">
              <div className="renewal-detail-card-header">
                <Title>Renewal Details</Title>
              </div>
              <div className="renewal-info-grid">
                <div className="info-item">
                  <span className="info-label">Request ID:</span>
                  <span className="info-value">{renewalDetails?.idx ? `R${String(renewalDetails.idx).padStart(3, '0')}` : '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Quantity:</span>
                  <span className="info-value">{renewalDetails?.quantity || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status:</span>
                  <span className="info-value status-badge">{renewalDetails?.status || '-'}</span>
                </div>
              </div>
            </div>

            <div className="renewal-status-card">
              <Title>Renewal Status</Title>
              <div className="renewal-timeline-container">
                <div className="timeline-points-container">
                  {renderTimelinePoints()}
                </div>
              </div>
            </div>

            <div className="renewal-devices-card">
              <div className="devices-header">
                <Title>Devices ({devicesData.length})</Title>
                <OptionList
                  name="Device List"
                  Icon={DownloadIcon}
                  options={downloadOptions}
                  onOptionClick={handleDownload}
                  customClassName="device-list-dropdown"
                />
              </div>
              <div className="renewal-devices-table-wrapper">
                <ReactTable
                  data={devicesData}
                  columns={devicesColumns}
                  showSearch={false}
                  pageSize={10}
                  showSelection={false}
                />
              </div>
            </div>
          </>
        )}
      </article>

      <AddPayment
        isOpen={isAddPaymentModalVisible}
        closeHandler={closeHandler}
        handleAdd={onPaymentAdded}
        isOrdersAddPayment={true}
        isUploadImageVisible={true}
        orderId={renewalDetails?.idx}
        formClass="submit-payment-dets-modal"
        handleApiSubmission={false}
      />

      <Modal size="4xl" isOpen={showPaymentDetails} onClose={() => setShowPaymentDetails(false)} isCentered={true}>
        <ModalOverlay />
        <ModalContent className="payment-details-modal">
          <ModalCloseButton />
          <Title variant="md" fixed>Payment Details</Title>
          {isPaymentDetailAvailable && paymentDetails ? (
            <div className="payment-details">
              <div className="details-container">
                <p>
                  <span className="key">Transaction ID:</span>
                  <span className="value">{paymentDetails.refNo || "-"}</span>
                </p>
                <p>
                  <span className="key">Amount Paid:</span>
                  <span className="value">₹{paymentDetails.amt ? paymentDetails.amt.toLocaleString('en-IN') : "-"}</span>
                </p>
                <p>
                  <span className="key">Payment Date:</span>
                  <span className="value">{paymentDetails.ts ? getLocalisedDate(paymentDetails.ts, true) : "-"}</span>
                </p>
              </div>
              {paymentDetails.ssUrl ? (
                <button
                  className="view-screenshot"
                  style={{ color: 'var(--ButtonPrimary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                  onClick={() => {
                    let url = paymentDetails.ssUrl;
                    if (url && !(url.startsWith('http') || url.startsWith('/'))) {
                      url = '/' + url;
                    }
                    window.open(url, '_blank', 'noopener');
                  }}
                  type="button"
                >
                  View Screenshot
                </button>
              ) : null}
            </div>
          ) : (
            <p className="no-payment-details">No payment details available</p>
          )}
        </ModalContent>
      </Modal>
    </section >
  );
};

export default RenewalDetail;
